"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _stencilReactDesc = require("@amzn/stencil-react-desc");
var _AriaHiddenManager = require("../helpers/AriaHiddenManager");
var _Portal = _interopRequireDefault(require("../helpers/Portal"));
var _events = require("../helpers/events");
var _useStencilPxtTelemetry = require("../pxt-telemetry/useStencilPxtTelemetry");
var _helpers = require("../helpers");
var _layout = require("../layout");
var _pxtTelemetry = require("../pxt-telemetry");
var _responsive = require("../responsive");
var _utils = require("../utils");
var _PopoverController = _interopRequireDefault(require("./PopoverController"));
var _constants = require("./constants");
var _propTypes = require("./propTypes");
var _utils2 = require("./utils");
var _react2 = require("@emotion/react");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const PopoverWithoutDocs = _ref => {
  let {
    'aria-haspopup': ariaHasPopup,
    children,
    contentProps: passedContentProps,
    maxWidth: maxWidthProp,
    onClosed,
    onOpened,
    position = _constants.popoverDefaultProps.position,
    shouldCloseOnFocusLeave = _constants.popoverDefaultProps.shouldCloseOnFocusLeave,
    shouldFocusOnOpen = _constants.popoverDefaultProps.shouldFocusOnOpen,
    triggerButtonProps = _constants.popoverDefaultProps.triggerButtonProps,
    triggerText,
    ...forwardedProps
  } = _ref;
  const [isOpen, setIsOpen] = (0, _react.useState)(false);
  const {
    addAriaHiddenOnSiblings,
    removeAriaHiddenOnSiblings
  } = (0, _AriaHiddenManager.useAriaHiddenOnSiblings)();
  const target = (0, _react.useRef)(null);
  const viewport = (0, _responsive.useBreakpoints)();
  const isSmallViewport = viewport.matches.s;
  const parentRef = (0, _react.useRef)(null);
  const dialogRef = (0, _react.useRef)(null);
  const handleSetIsOpen = value => {
    setIsOpen(value);
    (0, _events.setHasOpenedPopover)(value);
  };
  const handleToggle = () => {
    handleSetIsOpen(!isOpen);
  };
  const handleClose = () => {
    if (isDialogPattern) {
      var _target$current;
      removeAriaHiddenOnSiblings();
      (_target$current = target.current) === null || _target$current === void 0 || _target$current.focus();
    }
    handleSetIsOpen(false);
  };
  const handleKeyUp = e => {
    if (e.key === _utils.EventKeys.ESCAPE) {
      handleClose();
    }
  };

  // The `role` of the content must match the value of `aria-haspopup`.
  // Special case: `true` or "true" must be treated as "menu"
  // https://www.w3.org/TR/wai-aria/#aria-haspopup
  let contentRole;
  switch (ariaHasPopup) {
    case true:
    case 'true':
    case 'menu':
      contentRole = _constants.MENU_CONTENT_ROLE;
      break;
    default:
      contentRole = 'dialog';
  }
  const isDialogPattern = shouldFocusOnOpen && contentRole === 'dialog';
  const defaultMaxWidth = isSmallViewport ? _constants.MAX_WIDTH_SMALL_VIEWPORT : _constants.MAX_WIDTH_LARGE_VIEWPORT;
  const maxWidth = maxWidthProp === null ? undefined : maxWidthProp || defaultMaxWidth;
  const maxHeight = isSmallViewport ? _constants.MAX_HEIGHT_SMALL_VIEWPORT : _constants.MAX_HEIGHT_LARGE_VIEWPORT;
  const ariaHasPopupAttribute = contentRole === _constants.MENU_CONTENT_ROLE ? {
    'aria-haspopup': ariaHasPopup
  } : {}; // 'aria-describedby' should be used by the consumers in triggerButtonProps.

  const triggerPatternProps = isDialogPattern ?
  // Dialog pattern: https://www.w3.org/TR/wai-aria-practices-1.2/#dialog_modal.
  {
    shouldSetAriaExpanded: false
  } :
  // Disclosure button pattern: https://www.w3.org/TR/wai-aria-practices-1.2/#disclosure.
  // Adding aria-haspopup for the menu button.
  {
    ...ariaHasPopupAttribute,
    'aria-expanded': isOpen,
    shouldSetAriaExpanded: true
  };
  const contentPatternProps = shouldFocusOnOpen ? {
    role: contentRole,
    'aria-modal': contentRole === _constants.MENU_CONTENT_ROLE ? undefined : true,
    tabIndex: contentRole === _constants.MENU_CONTENT_ROLE ? undefined : -1
  } : {};
  const contentProps = {
    dataTestComponent: 'StencilPopoverContent',
    padding: _constants.CONTENT_PADDING,
    maxWidth,
    maxHeight,
    overflow: 'auto',
    ...passedContentProps,
    ...contentPatternProps,
    children: children({
      close: handleClose
    })
  };
  const [pxtTelemetryPath, pxtTelemetryTargetRef] = (0, _useStencilPxtTelemetry.useStencilPXTTelemetryPortalPatch)();
  const forwardedPXTTelemetryProps = (0, _pxtTelemetry.exposePxtTelemetryDataProps)(forwardedProps);

  // TODO: test focus trap with shouldCloseOnFocusLeave prop
  const popoverController = (0, _react2.jsx)(_PopoverController.default, {
    target: target,
    close: handleClose,
    onClosed: onClosed,
    onOpened: () => {
      if (isDialogPattern) {
        addAriaHiddenOnSiblings(parentRef.current);
      }
      if (!!onOpened) onOpened();
    },
    isOpen: isOpen,
    position: position,
    shouldFocusOnOpen: isDialogPattern ? false : shouldFocusOnOpen,
    shouldCloseOnFocusLeave:
    // when in dialog pattern the focus needs to go on the element with the role "dialog"
    isDialogPattern ? false : shouldCloseOnFocusLeave
  }, isDialogPattern ? (0, _react2.jsx)("div", pxtTelemetryPath, (0, _react2.jsx)(_helpers.FocusTrap, {
    active: isOpen,
    clickOutsideDeactivates: (0, _utils2.handlePopoverClickOutside)(shouldCloseOnFocusLeave ? target.current : undefined, parentRef.current),
    onDeactivate: handleClose
  }, (0, _react2.jsx)(_layout.View, (0, _extends2.default)({}, contentProps, {
    ref: dialogRef
  })))) : (0, _react2.jsx)(_layout.View, contentProps));
  return (0, _react2.jsx)(_react.default.Fragment, null, (0, _react2.jsx)(_helpers.TriggerButton, (0, _extends2.default)({}, triggerPatternProps, triggerButtonProps, {
    onClick: handleToggle,
    onKeyUp: handleKeyUp,
    isExpanded: isOpen,
    ref: target
  }, (0, _utils.pickDataProps)(forwardedProps)), triggerText), isDialogPattern ? (0, _react2.jsx)("div", (0, _extends2.default)({
    ref: pxtTelemetryTargetRef
  }, forwardedPXTTelemetryProps, {
    "data-test-component": "StencilPopoverContainer"
    // To avoid edge case related to the css flex wrapping it
    ,
    style: {
      display: 'none'
    }
  }), (0, _react2.jsx)(_Portal.default, null, (0, _react2.jsx)("div", {
    ref: parentRef
  }, popoverController))) : popoverController);
};
const PopoverWithDocs = (0, _stencilReactDesc.describe)(PopoverWithoutDocs).description('Displays a popover attached to a trigger button.');
PopoverWithDocs.propTypes = {
  ..._propTypes.popoverPropTypes
};
PopoverWithDocs.defaultProps = {
  ..._constants.popoverDefaultProps
};
const Popover = (0, _utils.withTestAttributes)({
  component: 'StencilPopoverTrigger'
})(PopoverWithDocs);
Popover.displayName = 'Popover';
var _default = exports.default = Popover;